// Stroke icon set. Emoji glyphs were the biggest tell that this UI was assembled
// rather than designed — they render differently per platform, don't sit on the
// text baseline, and can't inherit theme colour. These do all three.
export type IconName =
  | 'board'
  | 'users'
  | 'pencil'
  | 'list'
  | 'releases'
  | 'chart'
  | 'settings'
  | 'search'
  | 'plus'
  | 'flag'
  | 'blocked'
  | 'bug'
  | 'close'
  | 'check'
  | 'chevron'
  | 'chevronLeft'
  | 'arrow'
  | 'link'
  | 'unlink'
  | 'package'
  | 'coin'
  | 'trash'
  | 'bell'
  | 'bell-filled'
  | 'comment'
  | 'bolt'
  | 'device'
  | 'calendar'
  | 'document'
  | 'signature'
  | 'eye'
  | 'printer'
  | 'upload'
  | 'download'
  | 'sparkle'
  | 'clock'
  | 'star'
  | 'star-filled'
  | 'wrench'
  | 'task'
  | 'warning'
  | 'list-bullet'
  | 'list-ordered'
  | 'quote'
  | 'table'
  | 'table-col-add'
  | 'table-col-del'
  | 'table-row-add'
  | 'table-row-del'
  | 'table-remove'
  | 'sort'
  | 'grip'
  | 'mail'
  | 'pin'
  | 'pin-filled'
  | 'resize'
  | 'userPlus'
  | 'person'
  | 'briefcase'
  | 'building'
  | 'globe'
  | 'shield'
  | 'arrow-right'
  | 'grid'
  | 'lock'
  | 'info'
  | 'ellipsis'
  | 'collapse'
  | 'expand'
  | 'help';

const PATHS: Record<IconName, string> = {
  // An envelope — a signer who has no account here and is reached by email. Set
  // apart from a person's avatar and a role's people-glyph so a chip's kind reads
  // from its shape alone.
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  // A caution triangle with a bang — an at-risk note (a card due past its phase,
  // a report's risk banner). Stroke, on the baseline, taking the flag colour.
  warning: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>',
  // An assistant holding a card. Initials would read as a person.
  sparkle: '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/>',
  // A card carrying a conversation looked identical to one with none, so there
  // was no way to tell without opening every card in turn.
  comment: '<path d="M21 11.5a8 8 0 0 1-8 8H8.5L4 22l1.2-3.6A8 8 0 1 1 21 11.5z"/>',
  // A board is a frame divided into columns. The previous drawing was three
  // loose rectangles of different heights, the last of them 2px wide — which at
  // sidebar size read as two blocks and a stray line rather than as a board.
  board:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16M15 4v16"/>',
  list: '<path d="M4 6h16M4 12h16M4 18h10"/>',
  releases: '<path d="M12 2 3 7v10l9 5 9-5V7z"/><path d="M3 7l9 5 9-5M12 12v10"/>',
  chart: '<path d="M4 20V10M10 20V4M16 20v-7M21 20H3"/>',
  settings:
    '<circle cx="12" cy="12" r="3.2"/><path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.6-3.6"/>',
  users:
    '<circle cx="9" cy="8" r="3.4"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16.5 5.2a3.4 3.4 0 0 1 0 5.6M18 14.4a6.5 6.5 0 0 1 3.5 5.6"/>',
  pencil: '<path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z"/><path d="M14.5 6.5l3 3"/>',
  // A person with a plus — nominate somebody. One head-and-shoulders like `users`
  // but single, with a small plus to its upper right, so "add a person to stand in
  // for me" reads from the shape. Used for the empty "When I'm away" cover state.
  userPlus:
    '<circle cx="9" cy="8" r="3.4"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M18 6v5M15.5 8.5h5"/>',
  // One head-and-shoulders, centred — a single person. Marks the RESPONSIBLE (the
  // doer) on a card foot, beside the shield that marks an Accountable fallback, so
  // "who is shown" reads from the glyph's shape, not colour (two-part assignee).
  person:
    '<circle cx="12" cy="8.5" r="3.5"/><path d="M5.5 19.5a6.5 6.5 0 0 1 13 0"/>',
  // A briefcase — being away from work. The "When I'm away" account section, where
  // a person nominates who signs their sign-offs while they are on leave. A distinct
  // glyph so no two account-nav items share one (see the note by SECTIONS).
  briefcase:
    '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  // An office building — an external company. The Vendor name-tag (Feature B, B-07):
  // a supplier's own firm, beside their name. A clean tower outline with a door that
  // reads at the tag's small size without filling in; no window detail at 10.5px.
  building:
    '<path d="M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M15 21V9h3a1 1 0 0 1 1 1v11M3 21h18M9 21v-4h3v4"/>',
  // A globe — something that spans the whole workspace, not one board. The working
  // calendar's scope pill ("One calendar for the whole workspace", 0216). A circle
  // with one meridian and the equator, which read at pill size without window detail.
  globe: '<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.3 2.3 3.5 5.3 3.5 8.5s-1.2 6.2-3.5 8.5c-2.3-2.3-3.5-5.3-3.5-8.5s1.2-6.2 3.5-8.5z"/>',
  // A shield — an admin-arranged act, carrying authority. The "Arranged by (admin)"
  // pill on a cover somebody's admin set up for them (sign-offs R1, 0130).
  shield: '<path d="M12 3l7 3v5c0 4.6-3 7.8-7 9-4-1.2-7-4.4-7-9V6z"/>',
  // A rightward arrow — one step leading to the next. The "how the three connect"
  // vendor explainer (B-08) draws it between its cards.
  'arrow-right': '<path d="M5 12h13M13 6l6 6-6 6"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  flag: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z"/><path d="M4 22v-7"/>',
  // A no-entry sign: this card is held up. Replaces the ⛔ emoji in the Blocked
  // chip — a stroke glyph that sits on the baseline and takes the flag colour
  // like every other icon here. Vinnie, 11 Sep 2026: the emoji "looks cheap".
  blocked: '<circle cx="12" cy="12" r="8.5"/><path d="M6 6l12 12"/>',
  bug: '<path d="M8 6a4 4 0 0 1 8 0"/><rect x="6" y="7" width="12" height="12" rx="6"/><path d="M2 12h4M18 12h4M4 7l2.5 1.5M20 7l-2.5 1.5M4 17l2.5-1.5M20 17l-2.5-1.5"/>',
  close: '<path d="M6 6l12 12M18 6L6 18"/>',
  // A padlock — a control held shut: retention held back, a settled line with no
  // further action, a paid step that will not unlock until the gate is cleared.
  lock: '<rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/>',
  // A circled "i" — a recorded note that neither warns nor blocks, for the
  // segregation-of-duties caveat ("allowed, but recorded on the line").
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/>',
  check: '<path d="M4 12.5l5 5L20 6.5"/>',
  // A row's "more actions" handle — three solid dots, substantial enough to read
  // and to hit, never a hairline.
  ellipsis: '<circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.7" fill="currentColor" stroke="none"/>',
  chevron: '<path d="M9 6l6 6-6 6"/>',
  chevronLeft: '<path d="M15 6l-6 6 6 6"/>',
  arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3A5 5 0 0 0 13.5 3.5l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3A5 5 0 0 0 10.5 20.5l1.7-1.7"/>',
  // THE LINK, BROKEN — the baseline has been edited off the contract value, so the
  // two no longer move together. The same half-arcs as `link` with the join pulled
  // apart and the small break marks, for the held-contingency state.
  unlink: '<path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M5.17 11.75l-1.71 1.71a5 5 0 0 0 7.07 7.07l1.71-1.71"/><line x1="8" y1="2" x2="8" y2="5"/><line x1="2" y1="8" x2="5" y2="8"/><line x1="16" y1="19" x2="16" y2="22"/><line x1="19" y1="16" x2="22" y2="16"/>',
  // A SEALED BOX — a delivery milestone, a stage of work handed over. The standard
  // package cube; the kind glyph on the milestone register.
  package: '<path d="M16.5 9.4 7.5 4.2"/><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/>',
  // A COIN — a payment point: a billable milestone, the schedule-of-values marker.
  // A ring with a dollar stroke; used on the "Billable" tag and the reconciliation row.
  coin: '<circle cx="12" cy="12" r="9"/><path d="M12 6.8v10.4"/><path d="M14.6 9.1c-.5-.8-1.5-1.3-2.6-1.3-1.5 0-2.6.9-2.6 2s1.1 1.7 2.6 2 2.6.9 2.6 2-1.1 2-2.6 2c-1.1 0-2.1-.5-2.6-1.3"/>',
  bell: '<path d="M18 8.5a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5"/><path d="M13.7 20a2 2 0 0 1-3.4 0"/>',
  /*
   * THE BELL WITH SOMETHING IN IT — the same bell, filled, for when a person has
   * unread call-outs. The outline reads "caught up", the solid one "there is
   * something" at a glance, before the count is even read (Vinnie, 14 Sep — the
   * fill was the agreed signal and the bell was drawing the outline either way).
   * The clapper stays a stroke so the shape still reads as a bell.
   */
  'bell-filled':
    '<path d="M18 8.5a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5z" fill="currentColor" stroke="currentColor"/><path d="M13.7 20a2 2 0 0 1-3.4 0" fill="none"/>',
  trash: '<path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13"/>',
  // The one action worth doing before any other. Filled rather than stroked —
  // at 13px a hollow bolt reads as a scribble, and the mockup draws it solid.
  bolt: '<path d="M13.5 2 4 13.5h6L9.5 22 20 10.5h-6.7z" fill="currentColor" stroke="none"/>',
  // A screen on a stand. Distinct from `board` at a glance, which two lists of
  // places sitting one above the other in the same menu have to be.
  device: '<rect x="2.5" y="4" width="19" height="12.5" rx="2"/><path d="M9 20.5h6M12 16.5v4"/>',
  // A stretch of time. Deliberately unlike `board` — both sit in the same menu
  // and a sprint is a fortnight, not a set of columns, so the drawing says so:
  // two rings, a bar, and days rather than vertical dividers.
  calendar:
    '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  /*
   * A SHEET WITH A CORNER TURNED DOWN. Documents and Backlog both drew `list`,
   * so two entries one above the other in the same menu were the same picture —
   * which is no icon at all. Deliberately not lined like a page: at 16px the
   * rules inside collapse into grey and it becomes `list` again.
   */
  document: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/>',
  /*
   * A PEN SIGNING, OVER THE STROKE IT LEAVES. Sign-offs sat directly above
   * Documents in the menu and both drew the turned-corner sheet — two entries,
   * one picture, which is no icon at all (Vinnie, 5 September). A sign-off is a
   * signature, so the glyph is a pen on its diagonal with the signed swoosh
   * beneath it — nothing sheet-shaped, so it cannot be mistaken for Documents.
   */
  signature: '<path d="M4 21c2.6-2 5.4-2 8 0"/><path d="M17 4l3 3-9.5 9.5L6 18l1.5-4.5z"/><path d="M14.5 6.5l3 3"/>',
  eye: '<path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"/><circle cx="12" cy="12" r="2.6"/>',
  printer:
    '<path d="M7 8V3h10v5"/><path d="M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><rect x="7" y="14" width="10" height="7" rx="1"/>',
  // Arrow leaving the tray for upload, entering it for download — one drawing
  // reversed, so the pair reads as a pair rather than as two unrelated glyphs.
  upload: '<path d="M12 16V4"/><path d="M7.5 8.5L12 4l4.5 4.5"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>',
  download: '<path d="M12 4v12"/><path d="M7.5 11.5L12 16l4.5-4.5"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>',
  // How long a card has sat where it is — the aging marker on a working card.
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 8v4l3 2"/>',
  // The mark a Building card carries, the counterpart to the bug the Fixing
  // types wear — so a column's mix of work reads at a glance, not only its bugs.
  star: '<path d="M12 3l2.4 5.2 5.6.6-4.2 3.8 1.2 5.6L12 15.4 6.8 18l1.2-5.6L3.8 8.6l5.6-.6z"/>',
  // The same star, filled — the "this is a favourite" state, so a favourited row
  // reads at a glance before the name (Vinnie, 22 Oct). Petrol (via currentColor)
  // keeps it clear of the RAG health dot's green/amber/red.
  'star-filled':
    '<path d="M12 3l2.4 5.2 5.6.6-4.2 3.8 1.2 5.6L12 15.4 6.8 18l1.2-5.6L3.8 8.6l5.6-.6z" fill="currentColor" stroke="currentColor" stroke-linejoin="round"/>',
  // The Upkeep mark (tech debt, maintenance) — the third of the three Means.
  wrench: '<path d="M14 7a4 4 0 0 1-5 5l-5 5 2 2 5-5a4 4 0 0 0 5-5z"/>',
  /*
   * THE UPKEEP MARK, CLEARLY — a checkbox, the universal "task". Replaces the
   * wrench, which at 11px on the card read as an unrecognizable blob, and the
   * gear (`settings`) the opened card drew instead — two glyphs for one type
   * (Vinnie, 14 September). A rounded box with a proportional check, legible at
   * card size. Both views draw this one, via `meansMark` in issue-types.
   */
  task: '<rect x="3.5" y="3.5" width="17" height="17" rx="3.5"/><path d="M8 12.2l2.8 2.8L16.5 9"/>',
  /*
   * THE RICH-TEXT TOOLBAR ICONS — the shared editor's formatting controls, drawn
   * in the same stroke hand as the rest so the charter document and the About
   * scope fields look designed, not labelled. Bold/italic/strike and the heading
   * levels stay letter-marks (drawn in the toolbar itself) — those ARE the
   * universal glyphs; everything else becomes a picture.
   */
  // A list with round markers — the dots are filled so they read as bullets, not
  // as a fourth short line, at 15px.
  'list-bullet':
    '<circle cx="4.5" cy="7" r="1.3" fill="currentColor" stroke="none"/><circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="4.5" cy="17" r="1.3" fill="currentColor" stroke="none"/><path d="M9 7h11M9 12h11M9 17h11"/>',
  // The same list, numbered — a 1 and a 2 stand where the dots were, so the two
  // list buttons are told apart by their markers, not by a subtle shape.
  'list-ordered':
    '<path d="M9 7h11M9 12h11M9 17h11"/><path d="M4.1 5.6 5.3 5v4.2"/><path d="M3.6 14.9c.2-.9 1.9-.9 1.9.25 0 .9-1.8 1.45-1.95 2.75H5.7"/>',
  // Two opening quotation marks — a pulled quote / blockquote.
  quote:
    '<path d="M9.6 7.5H6.4a1 1 0 0 0-1 1v2.4a1 1 0 0 0 1 1h1.9c0 1.7-.6 2.4-1.9 3.1"/><path d="M18.6 7.5h-3.2a1 1 0 0 0-1 1v2.4a1 1 0 0 0 1 1h1.9c0 1.7-.6 2.4-1.9 3.1"/>',
  // A ruled grid — insert a table (risk register, RACI, milestones).
  table:
    '<rect x="3" y="4.5" width="18" height="15" rx="1.8"/><path d="M3 9.5h18M3 14.5h18M9 4.5v15M15 4.5v15"/>',
  // The table-editing controls only appear when the caret is inside a table. Each
  // is a partial grid with a + or − on the axis it acts on, so add/remove and
  // row/column read from the glyph without the caption.
  'table-col-add':
    '<rect x="3.5" y="5" width="9" height="14" rx="1.4"/><path d="M8 5v14"/><path d="M18 9.5v5M15.5 12h5"/>',
  // Delete carries an × (not a mirrored −) so add and remove tell apart by shape,
  // not a 4px stroke — the same × the delete-table glyph uses, so every remove
  // control reads the same.
  'table-col-del':
    '<rect x="3.5" y="5" width="9" height="14" rx="1.4"/><path d="M8 5v14"/><path d="M16 10l4 4M20 10l-4 4"/>',
  'table-row-add':
    '<rect x="5" y="3.5" width="14" height="9" rx="1.4"/><path d="M5 8h14"/><path d="M12 15v5M9.5 17.5h5"/>',
  'table-row-del':
    '<rect x="5" y="3.5" width="14" height="9" rx="1.4"/><path d="M5 8h14"/><path d="M10 15.5l4 4M14 15.5l-4 4"/>',
  // A table struck through — delete the whole table.
  'table-remove':
    '<rect x="3" y="5" width="18" height="14" rx="1.8"/><path d="M3 12h6M9 5v14"/><path d="M13.8 9.8l4.4 4.4M18.2 9.8l-4.4 4.4"/>',
  // Descending bars — the universal "sort by" glyph (Material, Linear, ClickUp all
  // draw it): stacked lines getting shorter top-to-bottom. Not the up/down arrows,
  // which mean "flip a sortable column's direction" — this control picks the key
  // the cards are ordered by, so it wears the plain sort mark, not the swap arrows.
  sort: '<path d="M4 6h16M4 12h10M4 18h5"/>',
  // A six-dot drag grip — the universal "grab and move me" mark (Notion, Linear,
  // monday all draw it). Two columns of three filled dots, so it reads as a handle
  // rather than as bullet text at 18px.
  grip:
    '<circle cx="9" cy="6" r="1.6" fill="currentColor" stroke="none"/><circle cx="15" cy="6" r="1.6" fill="currentColor" stroke="none"/><circle cx="9" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="15" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="9" cy="18" r="1.6" fill="currentColor" stroke="none"/><circle cx="15" cy="18" r="1.6" fill="currentColor" stroke="none"/>',
  // A thumbtack, outline — the "pin this in place" control before it is pinned.
  pin:
    '<path d="M9 3h6l-1 5 3 3.5V13H8v-1.5L11 8z"/><path d="M12 13v8"/>',
  // The same thumbtack, filled — the calm "Pinned" state, so the chip reads as
  // set rather than as an offer.
  'pin-filled':
    '<path d="M9 3h6l-1 5 3 3.5V13H8v-1.5L11 8z" fill="currentColor" stroke="currentColor"/><path d="M12 13v8" fill="none"/>',
  // A resize gripper — two parallel diagonals in the corner, the mark every grid
  // tool draws bottom-right of a resizable cell.
  resize: '<path d="M10 20 20 10M15 20 20 15"/>',
  // A four-cell grid — the "arrange the panels" affordance (monday / Planner use
  // exactly this square-of-four for a customizable dashboard).
  grid:
    '<rect x="3.5" y="3.5" width="7" height="7" rx="1.4"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.4"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.4"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.4"/>',
  // A question mark in a ring — the "how do I…" Help entry. A ring (r9, matching
  // `blocked`) so it reads as the universal help mark, with the mark's dot on the
  // baseline like every other glyph here. Not an "i" — that is InfoTip's job
  // (point-of-need "what is this"); this is the way IN to "how do I".
  help:
    '<circle cx="12" cy="12" r="9"/><path d="M9.4 9.3a2.7 2.7 0 0 1 5.3 1c0 1.8-2.7 2.3-2.7 2.3"/><path d="M12 16.8h.01"/>',
  /*
   * FOLD THE ROWS UP TO A HEADER — the Collapse-all mark. Built from the app's own
   * tapering-rows vocabulary (the same shortening lines as `sort` and `list`) with
   * a chevron over them, so it belongs to this set rather than looking bolted on.
   * The chevron points UP: fold the lanes up into their one-line headers. Its
   * counterpart `expand` is the identical rows with the chevron pointing down.
   */
  collapse: '<path d="M8.5 7.5 12 4l3.5 3.5"/><path d="M5 11.5h14M5 15.5h9M5 19.5h5"/>',
  // OPEN THE ROWS BACK OUT — the Expand-all mark: the same tapering rows, chevron
  // pointing down, so the pair reads as one control in two states.
  expand: '<path d="M8.5 4 12 7.5 15.5 4"/><path d="M5 11.5h14M5 15.5h9M5 19.5h5"/>',
};

interface Props {
  name: IconName;
  size?: number;
  className?: string;
}

export default function Icon({ name, size = 16, className = '' }: Props) {
  return (
    <svg
      className={`ic ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  );
}
